/** @jsxImportSource theme-ui */
import React, { useMemo } from 'react';
import { ResponsiveStyleValue } from 'theme-ui';
import { SpaceSizing } from '../utils/types';
import { useBreakpointValue } from '../../hooks';
import { CarrotTheme } from '../../theme';

interface UseGridItemStylesProps {
    colStart?: ResponsiveStyleValue<number>;
    colEnd?: ResponsiveStyleValue<number>;
    colSpan?: ResponsiveStyleValue<number>;
    rowStart?: ResponsiveStyleValue<number>;
    rowEnd?: ResponsiveStyleValue<number>;
    rowSpan?: ResponsiveStyleValue<number>;
}
export interface GridItemProps extends UseGridItemStylesProps {
    as?: 'div' | 'aside' | 'main' | 'section' | 'nav';
}

function useGridItemStyles({ colStart, colEnd, colSpan, rowStart, rowEnd, rowSpan }: UseGridItemStylesProps) {
    const columnSpanStyle = useMemo(
        () => (Array.isArray(colSpan) ? colSpan : [colSpan]).map((span) => (span ? `span ${span}` : span)),
        [colSpan]
    );
    const { columnStartStyle, columnEndStyle } = useMemo(
        () => ({
            columnStartStyle: colStart || columnSpanStyle,
            columnEndStyle: colEnd || columnSpanStyle,
        }),
        [columnSpanStyle, colStart, colEnd]
    );

    const rowSpanStyle = useMemo(
        () => (Array.isArray(rowSpan) ? rowSpan : [rowSpan]).map((span) => (span ? `span ${span}` : span)),
        [rowSpan]
    );
    const { rowStartStyle, rowEndStyle } = useMemo(
        () => ({
            rowStartStyle: rowStart || rowSpanStyle,
            rowEndStyle: rowEnd || rowSpanStyle,
        }),
        [rowSpanStyle, rowStart, rowEnd]
    );

    const responsiveColStart = useBreakpointValue(columnStartStyle);
    const responsiveColEnd = useBreakpointValue(columnEndStyle);
    const responsiveRowStart = useBreakpointValue(rowStartStyle);
    const responsiveRowEnd = useBreakpointValue(rowEndStyle);

    let columnStyle, rowStyle;

    if (responsiveColStart && responsiveColEnd) {
        columnStyle = [responsiveColStart, responsiveColEnd].join(' / ');
    }
    if (responsiveRowStart && responsiveRowEnd) {
        rowStyle = [responsiveRowStart, responsiveRowEnd].join(' / ');
    }
    if (columnStyle && rowStyle) {
        return {
            gridArea: [responsiveRowStart, responsiveColStart, responsiveRowEnd, responsiveColEnd].join(' / '),
        };
    }
    if (columnStyle) {
        return {
            gridColumn: columnStyle,
        };
    }
    if (rowStyle) {
        return {
            gridRow: rowStyle,
        };
    }
    return {
        gridColumnStart: responsiveColStart,
        gridColumnEnd: responsiveColEnd,
        gridRowStart: responsiveRowStart,
        gridRowEnd: responsiveRowEnd,
    };
}

export function GridItem({
    colStart,
    colEnd,
    colSpan,
    rowStart,
    rowEnd,
    rowSpan,
    as: Component,
    ...otherProps
}: React.PropsWithChildren<GridItemProps>): JSX.Element {
    const styles = useGridItemStyles({ colStart, colEnd, colSpan, rowStart, rowEnd, rowSpan });
    return <Component sx={styles} {...otherProps} />;
}

GridItem.defaultProps = {
    colSpan: [],
    rowSpan: [],
    as: 'div',
};

interface UseGapStylesProps {
    gap?: ResponsiveStyleValue<SpaceSizing>;
    rowGap?: ResponsiveStyleValue<SpaceSizing>;
    columnGap?: ResponsiveStyleValue<SpaceSizing>;
}

function useSpacingStyles({ gap, rowGap, columnGap }: UseGapStylesProps) {
    const spacingRow = rowGap || gap;
    const spacingColumn = columnGap || gap;

    const rowResponsive = useBreakpointValue(spacingRow);
    const columnResponsive = useBreakpointValue(spacingColumn);
    return {
        // eslint-disable-next-line
        // @ts-ignore
        gap: (theme: CarrotTheme) => `${theme.space[columnResponsive]} ${theme.space[rowResponsive]}`,
    };
}

export interface GridProps extends UseGapStylesProps {
    templateRows: ResponsiveStyleValue<string>;
    templateColumns: ResponsiveStyleValue<string>;
    height?: ResponsiveStyleValue<string>;
}

export function Grid({
    gap,
    rowGap,
    columnGap,
    templateRows,
    templateColumns,
    height,
    ...otherProps
}: React.PropsWithChildren<GridProps>): JSX.Element {
    const gapStyles = useSpacingStyles({ gap, rowGap, columnGap });
    return (
        <div
            sx={{
                display: 'grid',
                gridTemplateRows: templateRows,
                gridTemplateColumns: templateColumns,
                height,
                ...gapStyles,
            }}
            {...otherProps}
        />
    );
}

Grid.defaultProps = {
    gap: 'medium',
};
